"use client";

import type { FC } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { PortfolioScreenshotImageSection } from "./PortfolioScreenshotImageSection";
import type { PortfolioScreenshotModalContentProps } from "./types";
import { usePortfolioScreenshotModal } from "@/src/hooks/onboarding/usePortfolioScreenshotModal";
import { useIsPc } from "@/src/hooks/useIsPc";

export const PortfolioScreenshotModalContent: FC<
  PortfolioScreenshotModalContentProps
> = ({ uploadType = "image", initialFiles, onClose }) => {
  const [isPc] = useIsPc();
  const modal = usePortfolioScreenshotModal({
    uploadType,
    initialFiles,
  });

  const handleClose = () => {
    onClose({ action: "cancel" });
  };

  const handlePrimary = () => {
    void (async () => {
      const confirmPayload = await modal.handlePrimaryClick();
      if (!confirmPayload) {
        return;
      }
      onClose({
        action: "confirm",
        ...confirmPayload,
      });
    })();
  };

  const chipNameClassName = isPc ? "w-[90px]" : "w-[88px]";
  const isBusy = modal.isRecognizing || modal.isSaving;

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        isPc
          ? "relative min-h-[300px] max-h-[90vh] overflow-hidden rounded-2 bg-background_solid_neutral_primary p-6"
          : "h-full bg-background_solid_neutral_secondary pb-safe pt-safe",
      )}
      data-testid="portfolio-screenshot-modal"
      data-layout={isPc ? "desktop" : "mobile"}
      data-upload-type={uploadType}
    >
      {isPc ? (
        <button
          type="button"
          className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center"
          aria-label="Close"
          data-testid="portfolio-screenshot-close"
          onClick={handleClose}
        >
          <Icon
            name="close"
            size={24}
            color={ospThemes.colors.content_primary}
          />
        </button>
      ) : (
        <div className="flex shrink-0 items-center justify-start px-4 pb-2 pt-2">
          <button
            type="button"
            className="flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]"
            aria-label="Close"
            data-testid="portfolio-screenshot-close"
            onClick={handleClose}
          >
            <Icon
              name="close"
              size={20}
              color={ospThemes.colors.content_primary}
            />
          </button>
        </div>
      )}

      <div
        className={cn(
          "flex min-h-0 flex-col gap-6 overflow-y-auto",
          isPc ? "flex-1" : "flex-1 px-4 pt-2",
        )}
      >
        <div className={cn("flex flex-col gap-2.5", isPc && "pr-8 gap-6")}>
          <h1
            className={cn(
              "text-content_primary",
              isPc ? "headline_regular_s" : "headline_regular_m",
            )}
          >
            {modal.copy.title}
          </h1>
          <p className="body_m text-content_tertiary">{modal.copy.subtitle}</p>
        </div>

        <PortfolioScreenshotImageSection
          images={modal.images}
          canAddMore={modal.canAddMore}
          recognizeError={modal.inlineError}
          chipNameClassName={chipNameClassName}
          onRemove={modal.handleRemove}
          onPickMore={() => {
            void modal.handlePickMore();
          }}
        />
      </div>

      <input
        ref={modal.photoInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={modal.handlePhotoChange}
      />
      {uploadType === "pdf" ? (
        <input
          ref={modal.documentInputRef}
          type="file"
          accept={modal.pdfAccept}
          multiple
          className="hidden"
          onChange={modal.handleDocumentChange}
        />
      ) : null}

      <div className={cn("shrink-0", isPc ? "pt-6" : "px-4 py-6")}>
        <button
          type="button"
          disabled={modal.isPrimaryDisabled && !isBusy}
          className={cn(
            "button_m flex h-11 w-full items-center justify-center gap-1 rounded-2",
            "text-content_inverse_primary",
            isBusy
              ? "bg-background_brand_overlay70"
              : "bg-background_brand_overlay100",
            modal.isPrimaryDisabled && !isBusy ? "opacity-50" : "",
          )}
          data-testid="portfolio-screenshot-primary"
          onClick={handlePrimary}
        >
          {isBusy ? (
            <Icon
              name="ic_loading2"
              size={20}
              className="animate-spin"
              color={ospThemes.colors.content_inverse_primary}
            />
          ) : (
            modal.primaryLabel
          )}
        </button>
      </div>
    </div>
  );
};
